/**
 * Wizard Package LOB form
 */
import * as React from "react";
import { inject, observer } from "mobx-react";
import { PackageWizardState } from "stores/wizard/state";
import { PackageConfig } from "stores/wizard/config";
import { formatDate } from "bernie-l10n";
import { FlightTypeCodeMap } from "stores/wizard/state/flight/typings";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { ACTION_DONE, ACTION_TOGGLE, getTracker, TrackingUtils } from "utils/TrackingUtils";
import { FlightType, PackagesSubLobs } from "stores/wizard/state/typings";
import { ClassCodes } from "components/shared/PreferredClassInput/PreferredClassInput";
import { PreferredAirline } from "src/components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { Experiment, isVariantEnabled } from "components/utility/experiment";
import { locKeys } from "./l10n";
import {
  LobCombinations,
  PackageLegInformation,
  PackageTab,
  WizardPackagePWAProps,
  WizardPackagePWAFTNodeOptions,
} from "./typings";
import { WizardPackagePWAView } from "./WizardPackagePWAView";
import { normalizeOptionsData } from "src/stores/FlexTemplateStore";

@inject("analytics", "wizardState", "routeStore", "flexModuleModelStore")
@observer
export class WizardPackagePWA extends React.Component<WizardPackagePWAProps> {
  private readonly packageSubLOBs: Map<string, string[]>;
  private readonly defaultPackageType: LobCombinations;
  private readonly _preferredClass = "preferred-class.";
  private readonly _partialStay = "hotel-partial-stay.";

  private tracker: TrackingUtils;
  private subLobsfromFM: string[];
  private hotelState = this.props.wizardState.hotelWizardState;
  private packageState = this.props.wizardState.packageWizardState;
  private options: WizardPackagePWAFTNodeOptions;

  constructor(props: WizardPackagePWAProps) {
    super(props);

    const { packageWizardState, thirdPartyPackageWizardState, globalWizardState } = this.props.wizardState;
    const { context, flexModuleModelStore, flexTemplateNode } = props;
    const model = flexModuleModelStore.getModel(flexTemplateNode.id);
    this.options = normalizeOptionsData(flexTemplateNode, model) as WizardPackagePWAFTNodeOptions;
    this.subLobsfromFM = this.options.packageTypes;

    this.togglePartialStay = this.togglePartialStay.bind(this);
    this.toggleStaySomewhereElse = this.toggleStaySomewhereElse.bind(this);
    this.toggleDriveSomewhereElse = this.toggleDriveSomewhereElse.bind(this);
    this.setFlightType = this.setFlightType.bind(this);
    this.packageSubLOBs = this.getPackageSubLOBs();
    this.defaultPackageType = this.getDefaultPackageType();
    packageWizardState.overrideConfig(() => {
      packageWizardState.config.elementId = flexTemplateNode.id;
      packageWizardState.config.heading.fixedTitle = this.options.fixedLobTitle;
      packageWizardState.config.lastSearch.useLastSearchForSingleLOB = this.options.useLastSearchForSingleLOB;
      thirdPartyPackageWizardState.config.lastSearch.useLastSearchForSingleLOB = this.options.useLastSearchForSingleLOB;
      packageWizardState.config.heading.lobSubtitleBreakPoint = this.options.lobSubtitleBreakPoint;
      packageWizardState.config.heading.hideLobTitle = this.options.hideLobTitle;
      packageWizardState.config.defaultTabEnabled = isVariantEnabled(
        context,
        Experiment.PWA_PACKAGE_WIZARD_3PP_DEFAULT_TAB
      );
      if (this.options.titleFromPageHeader) {
        packageWizardState.config.heading.differentiatedTitles = false;
      }
      const { includeSingleLOBsWithinPackages } = globalWizardState.config;

      if (includeSingleLOBsWithinPackages) {
        const multiLOBPackageWizardLabelKey = "wizard.packages.valuePropSubtitle.v2";
        packageWizardState.config.pillsLabelKey = multiLOBPackageWizardLabelKey;
      }
      packageWizardState.updateEssAdapterConfigPackageType(this.defaultPackageType);
    });
    this.props.wizardState.packageWizardState.localDateFormat = this.props.context.localDateFormat;

    if (globalWizardState.shouldUpdateCookie) {
      packageWizardState.updateStateFromCookie();
    }

    packageWizardState.updateDateFromConfig();
    this.updateSoftPackagesOnReflection();
    this.setDestination(context);

    packageWizardState.updateExperimentNewPackageVariant(props.context);

    packageWizardState.moduleName = this.options.flexComponentName;
    this.tracker = getTracker(packageWizardState.trackingEnabled, this.props.analytics, packageWizardState.moduleName);
  }

  componentDidUpdate() {
    this.updateSoftPackagesOnReflection();
  }

  public static getFlightInLegURLFragment(
    packageWizardState: PackageWizardState,
    packageConfig: PackageConfig
  ): string {
    const legsInfo: PackageLegInformation = WizardPackagePWA.getLegsInformation(packageWizardState, packageConfig);

    return `orig:${legsInfo.destinationTla};origId:${legsInfo.destinationId};dest:${legsInfo.originTla};destId:${legsInfo.originId};departure:${legsInfo.endDate}`;
  }

  public static getFlightOutLegURLFragment(
    packageWizardState: PackageWizardState,
    packageConfig: PackageConfig
  ): string {
    const legsInfo: PackageLegInformation = WizardPackagePWA.getLegsInformation(packageWizardState, packageConfig);

    return `orig:${legsInfo.originTla};origId:${legsInfo.originId};dest:${legsInfo.destinationTla};destId:${legsInfo.destinationId};departure:${legsInfo.startDate}`;
  }

  private getPackageSubLOBs = () => {
    const lobsFromFM = new Map<string, string[]>();
    const pkgSubLOBs = new Set<string>();
    const lobsAllowed: string[] = [];
    if (this.subLobsfromFM !== undefined) {
      if (this.subLobsfromFM.includes("flightshotelscars")) {
        pkgSubLOBs.add("flights");
        pkgSubLOBs.add("hotels");
        pkgSubLOBs.add("cars");
        lobsAllowed.push("fhc");
      }
      if (this.subLobsfromFM.includes("flightshotels")) {
        pkgSubLOBs.add("flights");
        pkgSubLOBs.add("hotels");
        lobsAllowed.push("fh");
      }
      if (this.subLobsfromFM.includes("hotelscars")) {
        pkgSubLOBs.add("hotels");
        pkgSubLOBs.add("cars");
        lobsAllowed.push("hc");
      }
      if (this.subLobsfromFM.includes("flightscars")) {
        pkgSubLOBs.add("flights");
        pkgSubLOBs.add("cars");
        lobsAllowed.push("fc");
      }
    } else {
      pkgSubLOBs.add("flights");
      pkgSubLOBs.add("hotels");
      pkgSubLOBs.add("cars");
      lobsAllowed.push("fhc", "fc", "fh", "hc");
    }

    lobsFromFM.set(
      "pills",
      Array.from(pkgSubLOBs).sort((s1, s2) => (s1 > s2 ? -1 : 1))
    );
    lobsFromFM.set("lobsAllowed", lobsAllowed);

    return lobsFromFM;
  };

  public updateSoftPackagesOnReflection = () => {
    const { subLOBState } = this.packageState;
    const { FLIGHT_HOTEL_CAR, HOTEL_CAR, FLIGHT_HOTEL } = PackagesSubLobs;

    switch (subLOBState) {
      case FLIGHT_HOTEL:
        this.hotelState.toggleAddAFlightCheckbox({ stateToForce: true });
        this.hotelState.toggleAddACarCheckbox({ stateToForce: false });
        break;
      case FLIGHT_HOTEL_CAR:
        this.hotelState.toggleAddAFlightCheckbox({ stateToForce: true });
        this.hotelState.toggleAddACarCheckbox({ stateToForce: true });
        break;
      case HOTEL_CAR:
        this.hotelState.toggleAddAFlightCheckbox({ stateToForce: false });
        this.hotelState.toggleAddACarCheckbox({ stateToForce: true });
        break;
      default:
        this.hotelState.toggleAddAFlightCheckbox({ stateToForce: false });
        this.hotelState.toggleAddACarCheckbox({ stateToForce: false });
    }
  };

  private getDefaultPackageType = (): LobCombinations => {
    const defaultLOB = PackagesSubLobs.FLIGHT_HOTEL;
    if (Object.values(this.packageSubLOBs.get("lobsAllowed")!).some((val) => val === defaultLOB))
      return defaultLOB as LobCombinations;
    else return this.packageSubLOBs.get("lobsAllowed")!.values().next().value as LobCombinations;
  };

  private static getLegsInformation(
    packageWizardState: PackageWizardState,
    packageConfig: PackageConfig
  ): PackageLegInformation {
    const rawFormatForSubmission = packageConfig.date.ISOSubmissionSupport
      ? packageConfig.date.format
      : packageWizardState.localDateFormat;

    return {
      originTla: packageWizardState.location.origin.metaData.ttla,
      originId: packageWizardState.location.origin.metaData.regionId,
      destinationTla: packageWizardState.location.destination.metaData.ttla,
      destinationId: packageWizardState.location.destination.metaData.regionId,
      startDate: formatDate(packageWizardState.date.start, { raw: rawFormatForSubmission }),
      endDate: formatDate(packageWizardState.date.end, { raw: rawFormatForSubmission }),
    };
  }

  private onSelectPreferredClassCode = (classCode: ClassCodes) => {
    this.props.wizardState.packageWizardState.setFlightClass(classCode);

    this.tracker.trackEvent(this._preferredClass + ACTION_DONE);
  };

  public onSelectPreferredAirline = (preferredAirline: PreferredAirline) => {
    this.props.wizardState.packageWizardState.setFlightAirline(preferredAirline);

    this.tracker.trackEvent(this._preferredClass + ACTION_DONE);
  };

  public toggleStaySomewhereElse() {
    this.props.wizardState.packageWizardState.toggleStaySomewhereElse();

    if (this.props.wizardState.packageWizardState.staySomewhereElse) {
      this.tracker.trackEvent("HP.ASO.FLIGHT:HOTEL-RT.FlexibleHotelDestination");
    }
  }

  public toggleDriveSomewhereElse() {
    this.props.wizardState.packageWizardState.toggleDriveSomewhereElse();
    if (this.props.wizardState.packageWizardState.driveSomewhereElse) {
      this.tracker.trackEvent("HP.ASO.PACKAGE.HC.FlexibleCarPickupDropOffLocation");
    }
  }

  public togglePartialStay() {
    this.props.wizardState.packageWizardState.togglePartialStay();

    this.tracker.trackEvent(this._partialStay + ACTION_TOGGLE);
  }

  public setFlightType() {
    const {
      wizardState: { packageWizardState, globalWizardState },
    } = this.props;
    const activeFlightType = globalWizardState.flightTypeCode;

    switch (activeFlightType) {
      case FlightType.ONE_WAY:
        packageWizardState.updateSubLOBFlightTypeState(FlightType.ONE_WAY);
        packageWizardState.updateOnewayFlag(true);

        return this.props.l10n.formatText(locKeys.flightOneWayText);
      case FlightType.MULTI_CITY:
        packageWizardState.updateSubLOBFlightTypeState(FlightType.MULTI_CITY);
        packageWizardState.updateOnewayFlag(false);

        return this.props.l10n.formatText(locKeys.flightMultiCityText);
      case FlightType.ROUND_TRIP:
      default:
        packageWizardState.updateSubLOBFlightTypeState(FlightType.ROUND_TRIP);
        packageWizardState.updateOnewayFlag(false);

        return this.props.l10n.formatText(locKeys.flightRoundTripText);
    }
  }

  public setDestination(context: ExtendedContextStore) {
    const searchContext = context.searchContext;

    if (
      searchContext.pageType === "Destination-Travel-Guides" &&
      searchContext.lob === "package" &&
      searchContext.location
    ) {
      this.props.wizardState.packageWizardState.updateDestination(
        searchContext.location.extendedName,
        searchContext.location.id
      );
    }
  }

  public onSubLOBSelection = (pwaSubLOB: string) => () => {
    this.packageState.updateSubLOBState(pwaSubLOB);
    this.updateSoftPackagesOnReflection();
  };

  public onFlightTypeUpdate = (pwaSubLOBFlightType: FlightType) => {
    this.props.wizardState.packageWizardState.setFlightType(pwaSubLOBFlightType || FlightType.ROUND_TRIP);

    const packageType = this.props.wizardState.packageWizardState.subLOBState || "fh";
    const flightTypeCode = FlightTypeCodeMap[pwaSubLOBFlightType] || FlightTypeCodeMap.default;

    this.tracker.trackEvent(`HP.ASO.PACKAGE.${packageType.toUpperCase()}.${flightTypeCode}`);
  };

  private getDefaultTab = () => {
    let isAllInclusiveDestination;
    if (this.props.wizardState.packageWizardState.config.defaultTabEnabled) {
      const { allInclusiveDefaultCountryCodes, countryCode, include3PP } = this.options;
      const packageWith3PP = include3PP ?? this.props.wizardState.globalWizardState.config.packageWith3PP;
      isAllInclusiveDestination =
        packageWith3PP && countryCode && allInclusiveDefaultCountryCodes?.includes(countryCode);
    }

    const defaultTabConfigured =
      this.options.defaultPackageSubLOB ??
      this.props.wizardState.thirdPartyPackageWizardState.config.defaultPackageForm;

    return isAllInclusiveDestination ? PackageTab.AllInclusiveTab : defaultTabConfigured;
  };

  public render() {
    return (
      <WizardPackagePWAView
        {...this.props}
        setFlightType={this.setFlightType}
        toggleStaySomewhereElse={this.toggleStaySomewhereElse}
        toggleDriveSomewhereElse={this.toggleDriveSomewhereElse}
        togglePartialStay={this.togglePartialStay}
        defaultPackageType={this.defaultPackageType}
        onSubLOBSelection={this.onSubLOBSelection}
        onFlightTypeUpdate={this.onFlightTypeUpdate}
        onSelectPreferredClassCode={this.onSelectPreferredClassCode}
        onSelectPreferredAirline={this.onSelectPreferredAirline}
        packageSubLOBs={this.packageSubLOBs}
        defaultTab={this.getDefaultTab()}
      />
    );
  }
}

export default WizardPackagePWA;
